import type { RouteRecordRaw } from "vue-router";

import {
  ArchiveBoxIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowsRightLeftIcon,
  BeakerIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  CalculatorIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon,
  HandRaisedIcon,
  IdentificationIcon,
  InboxArrowDownIcon,
  LightBulbIcon,
  LinkIcon,
  QuestionMarkCircleIcon,
  QueueListIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  ShareIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/vue/24/outline";
import { RELAY_IMPACT_FLAG } from "@shared/constants/featureFlags";
import { PERMISSIONS } from "@shared/constants/permissions";
import { createRouter, createWebHistory } from "vue-router";

import { navigationGuard } from "@/router/navigationGuard";
import { isPageView } from "@/router/pageView";
import {
  firstEnabledPortalSection,
  PORTAL_HOME,
  PORTAL_SECTION_ACCESS,
} from "@/router/portalSections";
import {
  isDualRepFeatureFlagEnabled,
  isFeatureFlagEnabled,
  isFinancialsMvpEnabled,
} from "@/services/analytics/featureFlags";
import { useAuthStore } from "@/stores/auth";
import { isChatAvailable } from "@/utils/chatAccess";
// Lazy route components (RAP-492) — these heavy views (~3.2k LOC, pulling exceljs/dompurify)
// were statically imported into the entry chunk; load them on demand to keep them out of
// the main bundle. Each is defined once and reused across route entries.
const AdminDashboard = () => import("@/views/admin/Dashboard.vue");
const CampaignOverview = () => import("@/views/campaigns/CampaignOverview.vue");
const Campaigns = () => import("@/views/campaigns/Campaigns.vue");
const CampaignsLayout = () => import("@/views/campaigns/layout.vue");
const SingleCampaign = () => import("@/views/campaigns/SingleCampaign.vue");
const Firms = () => import("@/views/firms/Firms.vue");
const FirmsLayout = () => import("@/views/firms/layout.vue");

/**
 * Authenticated routes — all routes here require auth by default.
 * Additional authorization (role, permission) is specified per-route via meta.
 */
const AUTH_ROUTES: RouteRecordRaw[] = [
  // Dashboard
  {
    component: () => import("@/views/Dashboard.vue"),
    meta: {
      icon: Squares2X2Icon,
      label: "Dashboard",
      showInMenu: true,
    },
    name: "Dashboard",
    path: "/",
  },

  /**
   * The referring portal (RAP-630) — Summary · Leads · Intake · Cases · Capital · Settings.
   *
   * A SWITCH, not an addition: a firm with `referringPortalEnabled` is served this INSTEAD OF
   * the handling-firm app, and `canAccessRoute` denies its users every route outside this
   * subtree (which is also what reduces the sidebar to these items, since the sidebar filters
   * through the same helper).
   *
   * **The landing page is Summary**, the first item in the nav (RAP-680). It was briefly Leads,
   * because Summary's tiles are computed from `CampaignSpend` and `CostAllocation` and are empty
   * until a firm files its first spend report; the product call is to land on Summary anyway.
   * The landing order lives in `portalSections.ts`, which both the guard's fallback
   * (`navigationGuard.ts`) and the `/portal` index route read, so they cannot disagree — and it
   * falls through to the next section a user can open when Summary is closed to them.
   *
   * ## Every section is behind a Mixpanel gate
   *
   * Each section carries `canAccess: PORTAL_SECTION_ACCESS.<section>` (see `portalSections.ts`), so
   * a section can be switched off without a deploy — the sidebar item and the URL both follow the
   * flag, because both read `canAccessRoute`. Flags fail closed: a gate missing from Mixpanel
   * hides its section. They are UX, not a data boundary — the portal APIs do not consult them.
   *
   * ## A detail page is a CHILD of its list
   *
   * Leads and Cases are each a component-less parent holding the list (`path: ""`) and its detail
   * page (`:id`). That nesting is what keeps the sidebar item lit on the detail page (RAP-680):
   * `SidebarItem` takes its active state from `useLink().isActive`, which matches on the route
   * record chain, not on a path prefix. While the detail routes were SIBLINGS of their lists,
   * `/portal/cases/:id` had no record in common with `/portal/cases`, so "Cases" went dark the
   * moment a row was opened. Vue Router treats a link to an empty-path child as active anywhere
   * under its parent, which is exactly "the section stays highlighted". The same nesting means a
   * detail page inherits its list's gate structurally rather than by repeating it.
   *
   * A sub-page added to a section later should go inside that section's `children` for the same
   * reason.
   *
   * Under `/portal` rather than at `/leads`, `/cases` … so the subtree is one thing the deny
   * rule can name, and so a handling-firm route added later cannot collide with it.
   *
   * ## Order and grouping are the design's, read off the frame
   *
   * Figma `8496:166993` (the Summary frame's sidenav): Summary ungrouped and first, then
   * ACQUIRE (Leads, Acquisition, Intake), REFERRED (Cases), FINANCES (Capital, Portfolio),
   * CONFIGURE (Settings), with Support pinned to the bottom. `buildNav` preserves declaration
   * order and emits a heading only when `navGroup` CHANGES, so the child order below IS the
   * rendered order — reordering these entries silently reorders the sidebar.
   *
   * Summary carries no `navGroup` on purpose: the design shows it above the first heading, and
   * an item with no group gets no heading.
   *
   * **Quality and Portfolio are deferred** (spec §9) and deliberately absent rather than
   * disabled — a menu of dead entries is worse than a short menu. Quality is in neither the nav
   * nor any frame; Portfolio is in the nav and undesigned. Note the design file disagrees with
   * itself on Quality: the Acquisition and Settings frames show it under REFERRED, the Summary
   * and Capital frames do not. Absent is the reading that cannot be wrong.
   *
   * Cases, Capital and Settings were `showInMenu: false` through the Leads-only MVP, when all
   * three were `PortalPlaceholder` stubs with no data model behind them. They are real screens
   * now (RAP-648 / RAP-650 / RAP-656) over real tables, so the flags are back on.
   *
   * The router guard is UX. Every portal API route carries `requireReferringPortal` server-side;
   * see backend/src/lib/organizations/referring-portal.ts.
   */
  {
    children: [
      {
        /**
         * `/portal` itself. Every section sits behind its own Mixpanel gate, so the landing page
         * is the first section that is ON (see `portalSections.ts`), not a fixed route. This is a
         * `beforeEnter` rather than the parent's `redirect`: a redirect resolves before the global
         * guard has waited for the flags, so it would always see them all off. When nothing is on,
         * the route renders its own "nothing enabled yet" page instead of redirecting in a loop.
         */
        beforeEnter: () => {
          const target = firstEnabledPortalSection();
          return target ? { name: target } : true;
        },
        component: () => import("@/views/referring-portal/PortalHome.vue"),
        meta: {
          label: "Referring Portal",
          referringPortal: true,
        },
        name: PORTAL_HOME,
        path: "",
      },
      {
        component: () => import("@/views/referring-portal/PortalSummary.vue"),
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.summary,
          // No `navGroup`: the design puts Summary above the first heading, and `buildNav`
          // emits a heading only for an item that has one.
          icon: Squares2X2Icon,
          label: "Summary",
          referringPortal: true,
          showInMenu: true,
        },
        name: "PortalSummary",
        path: "summary",
      },
      {
        // The section, not a page: no component, so its children render straight into the
        // portal layout's `<router-view>`. See "A detail page is a CHILD of its list" above.
        children: [
          {
            component: () => import("@/views/referring-portal/PortalLeads.vue"),
            // No `showInMenu` here — the parent is the nav item (and the router contract forbids
            // it on an empty-path child). The label is this page's document title.
            meta: { label: "Leads", referringPortal: true },
            name: "PortalLeads",
            path: "",
          },
          {
            component: () => import("@/views/referring-portal/PortalLeadDetail.vue"),
            // Still needs `referringPortal: true` or `canAccessRoute`'s deny-by-default shuts the
            // portal's own users out of it.
            meta: { label: "Lead Detail", referringPortal: true },
            name: "PortalLeadDetail",
            path: ":id",
          },
        ],
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.leads,
          // Figma `8496:166999` names this icon exactly: `Icon / arrow-right-start-on-rectangle`.
          // Not an inbox, and not the `-end-` variant, which points the arrow the other way.
          icon: ArrowRightStartOnRectangleIcon,
          label: "Leads",
          navGroup: "Acquire",
          referringPortal: true,
          showInMenu: true,
        },
        path: "leads",
      },
      {
        component: () => import("@/views/referring-portal/PortalAcquisition.vue"),
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.acquisition,
          icon: ChartBarIcon,
          label: "Acquisition",
          navGroup: "Acquire",
          referringPortal: true,
          showInMenu: true,
        },
        name: "PortalAcquisition",
        path: "acquisition",
      },
      {
        // The tort-specific page (Figma `8875:70541`) — Social Media Harm today; other torts get
        // pages of their own. Sits after Acquisition in Acquire; the frame drops Intake from the
        // group, which stays until that is decided.
        component: () => import("@/views/referring-portal/PortalCampaigns.vue"),
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.campaigns,
          icon: ScaleIcon,
          label: "Campaigns",
          navGroup: "Acquire",
          referringPortal: true,
          showInMenu: true,
        },
        name: "PortalCampaigns",
        path: "campaigns",
      },
      {
        component: () => import("@/views/referring-portal/PortalIntake.vue"),
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.intake,
          icon: ClipboardDocumentCheckIcon,
          label: "Intake",
          navGroup: "Acquire",
          referringPortal: true,
          showInMenu: true,
        },
        name: "PortalIntake",
        path: "intake",
      },
      {
        // "What Relay did for you" (RAP-741) — counts only, so funders see it too. Last in
        // Acquire: it reports on the leads the three entries above it list.
        component: () => import("@/views/relay-impact/RelayImpactPage.vue"),
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.relayImpact,
          icon: ShieldExclamationIcon,
          label: "Relay Impact",
          navGroup: "Acquire",
          pageTitle: "What Relay did for you",
          referringPortal: true,
          showInMenu: true,
        },
        name: "PortalRelayImpact",
        path: "relay-impact",
        props: { audience: "portal" },
      },
      {
        // Same shape as Leads: the section is the parent, the list and its detail its children.
        children: [
          {
            component: () => import("@/views/referring-portal/PortalCases.vue"),
            meta: { label: "Cases", referringPortal: true },
            name: "PortalCases",
            path: "",
          },
          {
            component: () => import("@/views/referring-portal/PortalCaseDetail.vue"),
            meta: { label: "Case Detail", referringPortal: true },
            name: "PortalCaseDetail",
            path: ":id",
            // The component takes `caseId`; the segment is `:id`, to match `leads/:id`. Mapping
            // it here keeps the component free of `useRoute`, so it stays unit-testable on a prop.
            props: (route) => ({ caseId: String(route.params.id) }),
          },
        ],
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.cases,
          icon: BriefcaseIcon,
          label: "Cases",
          navGroup: "Referred",
          referringPortal: true,
          showInMenu: true,
        },
        path: "cases",
      },
      {
        component: () => import("@/views/referring-portal/PortalCapital.vue"),
        meta: {
          canAccess: PORTAL_SECTION_ACCESS.capital,
          icon: CurrencyDollarIcon,
          label: "Capital",
          navGroup: "Finances",
          referringPortal: true,
          showInMenu: true,
        },
        name: "PortalCapital",
        path: "capital",
      },
      {
        component: () => import("@/views/referring-portal/PortalSettings.vue"),
        meta: {
          // The section's Mixpanel flag AND the funder rule — funders never see Settings at all
          // (spec 2026-09-24). Both live in `PORTAL_SECTION_ACCESS`, which the landing order
          // also reads, so a funder is never landed on Settings.
          canAccess: PORTAL_SECTION_ACCESS.settings,
          icon: Cog6ToothIcon,
          label: "Settings",
          navGroup: "Configure",
          referringPortal: true,
          showInMenu: true,
        },
        name: "PortalSettings",
        path: "settings",
      },
    ],
    component: () => import("@/views/referring-portal/ReferringPortalLayout.vue"),
    meta: {
      // Gates everyone ELSE out. The `referringPortal` flags above gate portal users IN — the
      // two are not redundant, they point in opposite directions.
      canAccess: () => useAuthStore().isReferringPortalEnabled,
      // Never rendered on its own (its index child always renders inside it), but the route-contract test
      // requires a label on every renderable non-public route and the layout IS renderable.
      label: "Referring Portal",
      referringPortal: true,
    },
    // No `showInMenu` on the parent: its children are the nav, and a "Portal" group wrapping
    // them would add a level the designs do not have.
    path: "/portal",
  },

  // Portfolio demo routes — visible only to demo orgs (isDemo === true).
  // See docs/superpowers/specs/2026-04-08-demo-portfolio-pages-design.md
  {
    children: [
      {
        component: () => import("@/views/portfolio/Portfolio.vue"),
        meta: { label: "Portfolio Home" },
        name: "Portfolio",
        path: "",
      },
      {
        component: () => import("@/views/portfolio/Financials.vue"),
        meta: {
          icon: CalculatorIcon,
          label: "Financials",
          showInMenu: true,
        },
        name: "PortfolioFinancials",
        path: "financials",
      },
      {
        component: () => import("@/views/portfolio/Acquisitions.vue"),
        meta: {
          icon: ChartBarIcon,
          label: "Acquisitions",
          showInMenu: true,
        },
        name: "PortfolioAcquisitions",
        path: "acquisitions",
      },
      {
        component: () => import("@/views/portfolio/Firms.vue"),
        meta: {
          icon: BuildingOfficeIcon,
          label: "Firms",
          pageTitle: "Firms (Portfolio)",
          showInMenu: true,
        },
        name: "PortfolioFirms",
        path: "firms",
      },
    ],
    component: () => import("@/views/portfolio/PortfolioLayout.vue"),
    meta: {
      canAccess: () => useAuthStore().isDemoOrg,
      icon: Squares2X2Icon,
      label: "Portfolio",
      showInMenu: true,
    },
    path: "/portfolio",
  },

  // Financials (RF MVP) — skunkworks, client-only (localStorage). Parent layout
  // + Financials / Acquisition / Handling-firm pages, mirroring the demo
  // portfolio split. Gated by the Mixpanel `financials_mvp` feature flag so we
  // can target specific (mostly internal) users without a schema change; the
  // nav item appears reactively once the flag resolves after identify. Force-on
  // locally (no Mixpanel token) via `localStorage.rf-financials-enabled`.
  {
    children: [
      {
        component: () => import("@/views/financials/FinancialsOverview.vue"),
        // Renderable route → needs a label (router contract). pageTitle is only
        // for when the browser title must DIFFER from the nav label; here it
        // would be identical, so the title falls back to label.
        meta: { label: "Financials" },
        name: "Financials",
        path: "",
      },
      {
        component: () => import("@/views/financials/Acquisition.vue"),
        meta: {
          icon: ChartBarIcon,
          label: "Acquisition",
          showInMenu: true,
        },
        name: "FinancialsAcquisition",
        path: "acquisition",
      },
      {
        component: () => import("@/views/financials/HandlingFirms.vue"),
        meta: {
          icon: BuildingOfficeIcon,
          label: "Handling firms",
          showInMenu: true,
        },
        name: "FinancialsFirms",
        path: "firms",
      },
    ],
    component: () => import("@/views/financials/FinancialsLayout.vue"),
    meta: {
      canAccess: () => isFinancialsMvpEnabled(),
      icon: CalculatorIcon,
      label: "Financials",
      showInMenu: true,
    },
    path: "/financials",
  },

  // Campaigns
  {
    children: [
      {
        component: Campaigns,
        meta: { label: "Campaigns" },
        name: "Campaigns",
        path: "",
      },
      {
        component: CampaignOverview,
        meta: { label: "Campaign Details" },
        name: "SingleCampaign",
        path: ":id",
      },
      {
        component: SingleCampaign,
        meta: { label: "Campaign with Referral Agency" },
        name: "CampaignWithRA",
        path: ":id/:raId",
      },
      {
        component: () => import("@/views/campaigns/CaseDetail.vue"),
        meta: { label: "Case Details" },
        name: "CaseDetail",
        path: ":campaignId/:raId/:caseId",
      },
      {
        component: () => import("@/views/campaigns/CaseAssets.vue"),
        meta: { label: "Case Assets" },
        name: "CaseAssets",
        path: ":campaignId/:raId/:caseId/assets",
      },
    ],
    component: CampaignsLayout,
    meta: { icon: ScaleIcon, label: "Campaigns", showInMenu: true },
    path: "/campaigns",
  },

  // Firms (formerly Referral Agencies)
  {
    children: [
      {
        component: Firms,
        meta: { label: "Firms" },
        name: "Firms",
        path: "",
      },
      {
        component: () => import("@/views/firms/FirmDetails.vue"),
        meta: { label: "Firm Details" },
        name: "FirmDetail",
        path: ":firmId",
        props: true,
      },
    ],
    component: FirmsLayout,
    meta: { icon: BuildingOfficeIcon, label: "Firms", showInMenu: true },
    path: "/firms",
  },

  // Data Sharing — what this firm shares with other firms, and the switch to stop it
  // (RAP-588). Two audiences, one screen: firm ADMINS, and an account provisioned for nothing
  // else.
  //
  // BOTH legs are permissions, mirroring `requireGrantAuditAuthority` server-side.
  // `VIEW_DATA_SHARING` is seeded onto `grant_auditor` universally; the
  // `ADMINISTER_USERS_CAMPAIGNS` leg stays because production assigns admin permissions per
  // tenant, so an admin who was never granted the new one still reaches the screen. This used to
  // read `|| auth.isGrantAuditor` — a role name deciding what a user may SEE, which is what the
  // permission model exists to replace.
  {
    component: () => import("@/views/data-sharing/DataSharing.vue"),
    meta: {
      // The ONE route an audit-only account may reach — see `canAccessRoute`, which denies every
      // other route for that role so the sidebar and the guard agree. That denial is still
      // role-based, and has to be: permissions grant, they cannot subtract.
      allowGrantAudit: true,
      canAccess: () =>
        useAuthStore().hasAnyPermission([
          PERMISSIONS.VIEW_DATA_SHARING,
          PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS,
        ]),
      icon: ShareIcon,
      label: "Data Sharing",
      showInMenu: true,
    },
    name: "DataSharing",
    path: "/data-sharing",
  },

  // "What Relay did for you" (RAP-741) for a firm in the main app — a law firm sees the impact on
  // leads it received, a marketing partner on the leads it sent. Behind the `relay_impact` Mixpanel
  // gate (fail-closed), and only for a user with an org: the server answers for that org alone.
  // Portal firms reach the same page at /portal/relay-impact; superadmins at /superadmin/relay-impact.
  {
    component: () => import("@/views/relay-impact/RelayImpactPage.vue"),
    meta: {
      canAccess: () => !useAuthStore().isExternalUser && isFeatureFlagEnabled(RELAY_IMPACT_FLAG),
      icon: ShieldExclamationIcon,
      label: "Relay Impact",
      pageTitle: "What Relay did for you",
      showInMenu: true,
    },
    name: "RelayImpact",
    path: "/relay-impact",
    props: { audience: "firm" },
  },

  // Dual Rep / Duplicate Check — two-layer gate: the org's dualRepEnabled beta
  // setting (superadmin-managed; also enforced server-side on /claimant-matches)
  // AND the Mixpanel `dual_rep` feature flag for per-user rollout within enabled
  // orgs. Both default OFF and fail closed — hides both the nav item and URL
  // access. Force the Mixpanel layer on locally (no token) via
  // `localStorage.rf-dual-rep-enabled`.
  {
    component: () => import("@/views/duplicate-matches/DuplicateMatches.vue"),
    meta: {
      canAccess: () => useAuthStore().isDualRepEnabled && isDualRepFeatureFlagEnabled(),
      icon: UsersIcon,
      label: "Dual Rep/Duplicates",
      showInMenu: true,
    },
    name: "DualRepDuplicates",
    path: "/dual-rep-duplicates",
  },

  // Redirect /referring-firms to /firms for backward compatibility
  {
    path: "/referring-firms",
    redirect: "/firms",
  },
  {
    path: "/referring-firms/:id",
    redirect: (to) => `/firms/${to.params.id}`,
  },

  // Reports — any user with at least one usable shared view
  {
    children: [
      {
        component: () => import("@/views/reports/Reports.vue"),
        meta: { label: "Reports" },
        name: "Reports",
        path: "",
      },
    ],
    component: () => import("@/views/reports/layout.vue"),
    meta: {
      // Gated on view assignments, not role/org-type: anyone the firm has
      // shared a live view with can consume its reports.
      canAccess: () => useAuthStore().hasAssignedView,
      icon: DocumentChartBarIcon,
      label: "Reports",
      showInMenu: true,
    },
    path: "/reports",
  },

  // AI Assistance
  {
    children: [
      {
        path: "",
        // Never into a page the user is denied when the store is loaded (every in-app click).
        // On a cold load the redirect resolves before auth, so it lands on chat and the guard —
        // which waits for auth — bounces a denied user to the usual fallback.
        redirect: () =>
          isChatAvailable() ? "/ai-assistance/ai-chat" : "/ai-assistance/deep-insights",
      },
      {
        component: () => import("@/views/ai/AIChat.vue"),
        meta: {
          // The same predicate as the floating assistant (App.vue / SimpleLayout.vue): the
          // no-view_pii hide and the Mixpanel kill switch must close this page too.
          canAccess: () => isChatAvailable(),
          icon: ChatBubbleLeftRightIcon,
          label: "AI Chat",
          showInMenu: true,
        },
        name: "AIChat",
        path: "ai-chat",
      },
      {
        children: [
          {
            component: () => import("@/views/ai/DeepInsights.vue"),
            meta: { label: "Deep Insights" },
            name: "DeepInsights",
            path: "",
          },
          {
            component: () => import("@/views/ai/DeepInsightDetail/DeepInsightDetail.vue"),
            meta: { label: "Deep Insight Details" },
            name: "DeepInsightDetail",
            path: ":id",
            props: true,
          },
        ],
        meta: {
          // See canAccessDeepInsights (mirrors the backend preHandler; RAP-555).
          canAccess: () => canAccessDeepInsights(),
          icon: LightBulbIcon,
          label: "Deep Insights",
          showInMenu: true,
        },
        path: "deep-insights",
      },
    ],
    component: () => import("@/views/ai/layout.vue"),
    meta: {
      canAccess: () => {
        const a = useAuthStore();

        // External null-org users always pass; org members are gated by
        // their org's aiEnabled flag. Per-feature permission checks
        // (e.g. CORE_ACCESS for Deep Insights) live on the child routes — and the section
        // itself is shown only when at least one child is reachable, so a user denied both
        // (no-view_pii, kill switch) never gets a nav entry that only bounces them to Dashboard.
        return (
          (a.isExternalUser || a.isAiEnabled) && (isChatAvailable() || canAccessDeepInsights())
        );
      },
      icon: SparklesIcon,
      label: "AI Assistance",
      showInMenu: true,
    },
    path: "/ai-assistance",
  },

  // Case Health
  {
    children: [
      {
        component: () => import("@/views/case-health/CaseHealthDashboard.vue"),
        meta: { label: "Case Health" },
        name: "CaseHealth",
        path: "",
      },
    ],
    component: () => import("@/views/case-health/layout.vue"),
    meta: {
      /** TODO: Migrate to permissions */
      canAccess: () => {
        const a = useAuthStore();
        return a.isAiEnabled && a.isCaseHealthEnabled;
      },
      icon: ShieldCheckIcon,
      label: "Case Health",
      showInMenu: true,
    },
    path: "/case-health",
  },

  // Admin routes (HANDLING users only, except superadmin routes)
  {
    children: [
      {
        path: "",
        redirect: "/admin/campaigns",
      },
      {
        path: "standard-fields",
        redirect: "/admin/data-sources",
      },
      {
        children: [
          {
            component: AdminDashboard,
            meta: {
              // Mirror backend `GET /admin/campaigns` gate (campaigns/list.ts:64).
              // ADMINISTER_USERS_CAMPAIGNS is the admin-tier fallback for the
              // rare case of an admin role missing CORE_ACCESS — without it
              // here, those admins hit a router block on a page the backend
              // would have served them.
              canAccess: () =>
                useAuthStore().hasAnyPermission([
                  PERMISSIONS.CORE_ACCESS,
                  PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS,
                  PERMISSIONS.EXTERNAL_REPORTS,
                ]),
              label: "Campaigns",
              pageTitle: "Campaign Management",
            },
            name: "AdminDashboard",
            path: "",
          },
          {
            component: () => import("@/views/admin/EditCampaign.vue"),
            meta: {
              canAccess: () =>
                useAuthStore().hasAnyPermission([
                  PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS,
                  PERMISSIONS.ADMINISTER_AI,
                  PERMISSIONS.EXTERNAL_REPORTS,
                ]),
              label: "Edit Campaign",
            },
            name: "EditCampaign",
            path: ":id/edit",
            props: true,
          },
          {
            // Legacy standalone field-mappings page removed — its content lives in the
            // canonical campaign editor's "Campaign Fields" tab. Redirect any old
            // links/bookmarks there instead of 404-ing.
            path: ":id/fields",
            redirect: (to) => ({
              name: "EditCampaign",
              params: { id: to.params.id },
              query: { tab: "fields" },
            }),
          },
        ],
        meta: {
          icon: ScaleIcon,
          label: "Campaigns",
          pageTitle: "Campaign Management",
          showInMenu: true,
        },
        path: "campaigns",
      },
      {
        path: "crm-config",
        redirect: "/admin/data-sources",
      },
      {
        component: () => import("@/views/admin/AdminFeatures.vue"),
        meta: {
          canAccess: () => useAuthStore().hasPermission(PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS),
          icon: SparklesIcon,
          label: "Features",
          showInMenu: true,
        },
        name: "AdminFeatures",
        path: "features",
      },
      {
        path: "users",
        redirect: "/admin/users/internal",
      },
      {
        component: () => import("@/views/admin/AdminInternalUsers.vue"),
        meta: {
          canAccess: () =>
            useAuthStore().hasAnyPermission([
              PERMISSIONS.CORE_ACCESS,
              PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS,
            ]),
          icon: UsersIcon,
          label: "My Team",
          showInMenu: true,
        },
        name: "AdminInternalUsers",
        path: "users/internal",
      },
      {
        component: () => import("@/views/admin/AdminExternalUsers.vue"),
        meta: {
          canAccess: () => useAuthStore().hasPermission(PERMISSIONS.EXTERNAL_REPORTS),
          icon: UserGroupIcon,
          label: "External Users",
          showInMenu: true,
        },
        name: "AdminExternalUsers",
        path: "users/external",
      },
      {
        component: () => import("@/views/admin/AdminOrganizations.vue"),
        meta: {
          canAccess: () => useAuthStore().hasPermission(PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS),
          icon: BuildingOffice2Icon,
          label: "Organizations",
          showInMenu: true,
        },
        name: "AdminOrganizations",
        path: "organizations",
      },
      {
        component: () => import("@/views/admin/StageCategoriesView.vue"),
        meta: {
          canAccess: () => useAuthStore().hasPermission(PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS),
          icon: QueueListIcon,
          label: "Case Stages",
          showInMenu: true,
          title: "Case Stages",
        },
        name: "AdminStageCategories",
        path: "case-stages",
      },
      {
        children: [
          {
            component: () => import("@/views/admin/dataSources/DataSourcesOverview.vue"),
            meta: {
              canAccess: () => useAuthStore().hasPermission(PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS),
              label: "Data Sources",
            },
            name: "DataSourcesOverview",
            path: "",
          },
          {
            component: () => import("@/views/admin/dataSources/DataSourceDetail.vue"),
            meta: {
              canAccess: () => useAuthStore().hasPermission(PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS),
              label: "Data Source",
            },
            name: "DataSourceDetail",
            path: ":crmConfigId",
            props: true,
          },
        ],
        component: () => import("@/views/admin/dataSources/DataSourcesLayout.vue"),
        meta: {
          canAccess: () => useAuthStore().hasPermission(PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS),
          icon: CircleStackIcon,
          label: "Data Sources",
          pageTitle: "Data Source Management",
          showInMenu: true,
        },
        path: "data-sources",
      },
      {
        path: "ingestion",
        redirect: "/admin/data-sources",
      },
      {
        path: "ingestion/:rest(.*)*",
        redirect: "/admin/data-sources",
      },
    ],
    component: CampaignsLayout,
    meta: {
      /**
       * Parent gate is the union of every child route's permission check —
       * `canAccessRoute` AND's the matched chain, so a narrower parent
       * would block users a child would otherwise admit. Child routes
       * carry their own per-page permission predicates.
       */
      canAccess: () => {
        const a = useAuthStore();

        if (a.isDataSyncEnabled === false) {
          return false;
        }

        return a.hasAnyPermission([
          PERMISSIONS.CORE_ACCESS,
          PERMISSIONS.ADMINISTER_USERS_CAMPAIGNS,
          PERMISSIONS.ADMINISTER_AI,
          PERMISSIONS.EXTERNAL_REPORTS,
        ]);
      },
      icon: Cog6ToothIcon,
      label: "Admin",
      showInMenu: true,
    },
    path: "/admin",
  },

  // Backward-compat redirect: /admin/superadmin/* → /superadmin/*
  {
    path: "/admin/superadmin/:pathMatch(.*)*",
    redirect: (to) => to.fullPath.replace(/^\/admin\/superadmin/, "/superadmin"),
  },

  // Superadmin routes (superadmin users only)
  {
    children: [
      {
        component: () => import("@/views/admin/superadmin/Dashboard.vue"),
        // No nav entry of its own: the "Superadmin" parent item IS the dashboard link (an
        // empty-path child in the menu would duplicate it — router-contract.test.ts). RAP-742's
        // "(top) Dashboard" is that parent, above the first section heading.
        meta: { label: "Superadmin Dashboard" },
        name: "SuperadminDashboard",
        path: "",
      },
      {
        // One queue for everything RELAY reports that needs a person: held journey steps, refused
        // partner results and refused intake submissions, in plain words with working actions.
        component: () => import("@/views/admin/superadmin/AttentionQueue.vue"),
        meta: {
          icon: HandRaisedIcon,
          label: "Needs Attention",
          navGroup: "Referral Firms",
          showInMenu: true,
        },
        name: "SuperadminNeedsAHuman",
        path: "needs-a-human",
        props: (route) => ({
          category: typeof route.query.category === "string" ? route.query.category : undefined,
        }),
      },
      {
        // Intake rejections that need a person (our setup) live in the attention queue; a vendor's
        // bad data refused at the gate never alerts anyone. Old links land on the queue.
        name: "SuperadminIntakeRejections",
        path: "intake-rejections",
        redirect: { name: "SuperadminNeedsAHuman" },
      },
      {
        // Lead Intake (RAP-733): ONE page for getting a vendor's leads into RAP — lead mappings
        // (channel → campaign, which sets the mass tort) and question mappings (the vendor's
        // questions → the tort's standard questions). Leads whose answers couldn't be
        // standardized (RAP-734) are listed here too.
        component: () => import("@/views/admin/superadmin/LeadIntake.vue"),
        meta: {
          icon: InboxArrowDownIcon,
          label: "Lead Intake",
          navGroup: "Referral Firms",
          showInMenu: true,
        },
        name: "SuperadminLeadIntake",
        path: "lead-intake",
      },
      {
        // "What Relay did for you" (RAP-741) across every client, unattributed included, with a
        // "Showing" picker to view it exactly as one org does.
        component: () => import("@/views/relay-impact/RelayImpactPage.vue"),
        meta: {
          icon: ShieldExclamationIcon,
          label: "Relay Impact",
          navGroup: "Referral Firms",
          pageTitle: "What Relay did for you (all clients)",
          showInMenu: true,
        },
        name: "SuperadminRelayImpact",
        path: "relay-impact",
        props: { audience: "superadmin" },
      },
      {
        // One (vendor format, mass tort) pair's question mapping, opened from Lead Intake.
        component: () => import("@/views/admin/superadmin/QuestionMappingEditor.vue"),
        meta: {
          icon: ArrowsRightLeftIcon,
          label: "Question mapping",
          navGroup: "Referral Firms",
          showInMenu: false,
        },
        name: "SuperadminQuestionMappingEditor",
        path: "lead-intake/questions/:sourceFormatKey/:massTortId",
        props: true,
      },
      {
        // RAP-736: today's Social Media Harm charts vs the standardized ones, per firm — the check
        // before the `standardized_answers_charts` flag goes on. Opened from Lead Intake.
        component: () => import("@/views/admin/superadmin/StandardizedChartsCompare.vue"),
        meta: {
          icon: ArrowsRightLeftIcon,
          label: "Standardized charts check",
          navGroup: "Referral Firms",
          showInMenu: false,
        },
        name: "SuperadminStandardizedChartsCompare",
        path: "lead-intake/standardized-charts",
      },
      {
        // The former Lead Mappings page: now part of Lead Intake. Kept (and named) so old links
        // and bookmarks still land.
        name: "SuperadminLeadMappings",
        path: "lead-mappings",
        redirect: { name: "SuperadminLeadIntake" },
      },
      {
        // The former Question Mappings page. A link to one pair (?format=&tort=) opens that pair's
        // editor; anything else lands on Lead Intake.
        name: "SuperadminVendorMappings",
        path: "vendor-mappings",
        redirect: (to) => {
          const format = to.query.format;
          const tort = to.query.tort;
          return typeof format === "string" &&
            format !== "" &&
            typeof tort === "string" &&
            tort !== ""
            ? {
                name: "SuperadminQuestionMappingEditor",
                params: { massTortId: tort, sourceFormatKey: format },
                query: {},
              }
            : { name: "SuperadminLeadIntake", query: {} };
        },
      },
      {
        component: () => import("@/views/admin/superadmin/CrmArchive.vue"),
        meta: {
          icon: ArchiveBoxIcon,
          label: "CRM Archive",
          navGroup: "Handling Firms",
          pageTitle: "CRM Response Archive",
          showInMenu: true,
        },
        name: "SuperadminCrmArchive",
        path: "crm-archive",
      },
      {
        component: () => import("@/views/admin/superadmin/SmartFields.vue"),
        meta: {
          icon: SparklesIcon,
          label: "Smart Fields",
          navGroup: "Handling Firms",
          showInMenu: true,
        },
        name: "SuperadminSmartFields",
        path: "smart-fields",
      },
      {
        component: () => import("@/views/admin/superadmin/DataSharingGrants.vue"),
        meta: {
          icon: ShareIcon,
          label: "Data Sharing Grants",
          navGroup: "Handling Firms",
          showInMenu: true,
        },
        name: "SuperadminDataSharingGrants",
        path: "data-sharing-grants",
      },
      {
        component: () => import("@/views/admin/superadmin/MassTorts.vue"),
        meta: {
          icon: ScaleIcon,
          label: "Mass Torts",
          navGroup: "Mass Torts",
          showInMenu: true,
        },
        name: "SuperadminMassTorts",
        path: "mass-torts",
      },
      {
        // The list again, with the tort modal open on `:id` (see useMassTortModal). The modal
        // reads the id from the route, so no `props`.
        component: () => import("@/views/admin/superadmin/MassTorts.vue"),
        meta: {
          icon: ScaleIcon,
          label: "Mass Tort",
          navGroup: "Mass Torts",
          showInMenu: false,
        },
        name: "SuperadminMassTortDetail",
        path: "mass-torts/:id",
      },
      {
        // The standard questions every tort asks (RAP-732). Each tort's Questions tab shows them
        // read-only and links here.
        component: () => import("@/views/admin/superadmin/GeneralQuestions.vue"),
        meta: {
          icon: QuestionMarkCircleIcon,
          label: "General Questions",
          navGroup: "Mass Torts",
          showInMenu: true,
        },
        name: "SuperadminGeneralQuestions",
        path: "general-questions",
      },
      {
        component: () => import("@/views/admin/superadmin/Users/UsersView.vue"),
        meta: {
          icon: UserGroupIcon,
          label: "Users",
          navGroup: "Accounts",
          pageTitle: "Manage Users",
          showInMenu: true,
        },
        name: "SuperadminUsers",
        path: "users",
      },
      {
        component: () => import("@/views/admin/superadmin/OrganizationsTabs.vue"),
        meta: {
          icon: BuildingOffice2Icon,
          label: "Organizations",
          navGroup: "Accounts",
          pageTitle: "Manage Organizations",
          showInMenu: true,
        },
        name: "SuperadminOrganizations",
        path: "organizations",
      },
      {
        meta: {
          icon: IdentificationIcon,
          label: "Root Identities",
          navGroup: "Accounts",
          showInMenu: true,
        },
        name: "SuperadminRootIdentities",
        path: "root-identities",
        redirect: { name: "SuperadminOrganizations", query: { tab: "root-identities" } },
      },
      {
        component: () => import("@/views/admin/superadmin/MagicLinks.vue"),
        meta: {
          icon: LinkIcon,
          label: "Magic Links",
          navGroup: "Accounts",
          showInMenu: true,
        },
        name: "SuperadminMagicLinks",
        path: "magic-links",
      },
      {
        component: () => import("@/views/admin/superadmin/AiUsage.vue"),
        meta: {
          icon: ChartBarIcon,
          label: "AI Usage",
          navGroup: "AI",
          showInMenu: true,
        },
        name: "SuperadminAiUsage",
        path: "ai-usage",
      },
      {
        component: () => import("@/views/admin/superadmin/AiQueue.vue"),
        meta: {
          icon: QueueListIcon,
          label: "AI Queue",
          navGroup: "AI",
          showInMenu: true,
        },
        name: "SuperadminAiQueue",
        path: "ai-queue",
      },
      {
        component: () => import("@/views/ai-model-test/AIModelTestPage.vue"),
        meta: {
          icon: BeakerIcon,
          label: "AI Model Test",
          navGroup: "AI",
          showInMenu: true,
        },
        name: "AIModelTest",
        path: "ai-model-test",
      },
      {
        children: [
          {
            component: () => import("@/views/admin/superadmin/Utilities.vue"),
            meta: { label: "Utilities" },
            name: "SuperadminUtilities",
            path: "",
          },
          {
            component: () => import("@/views/admin/superadmin/TestEmail.vue"),
            meta: { label: "Email Utilities" },
            name: "SuperadminTestEmail",
            path: "test-email",
          },
          {
            component: () => import("@/views/admin/superadmin/Database.vue"),
            meta: { label: "Database" },
            name: "SuperadminDatabase",
            path: "database",
          },
          {
            component: () => import("@/views/admin/superadmin/AppDebug.vue"),
            meta: { label: "App Debug" },
            name: "SuperadminAppDebug",
            path: "app-debug",
          },
          {
            component: () => import("@/views/admin/superadmin/AwkoSandbox.vue"),
            meta: { label: "AWKO Sandbox" },
            name: "SuperadminAwkoSandbox",
            path: "awko-sandbox",
          },
        ],
        meta: {
          icon: WrenchScrewdriverIcon,
          label: "Utilities",
          navGroup: "Tools",
          showInMenu: true,
        },
        path: "utilities",
      },
    ],
    component: CampaignsLayout,
    meta: {
      canAccess: () => {
        const a = useAuthStore();
        return a.isSuperadmin;
      },
      icon: ShieldCheckIcon,
      label: "Superadmin",
      showInMenu: true,
    },
    path: "/superadmin",
  },

  // Terms acceptance (exempt from terms guard check)
  {
    component: () => import("@/views/auth/AcceptTerms/AcceptTerms.vue"),
    meta: {
      /**
       * A BOOTSTRAP route, and it must be reachable by the audit role or that account can never
       * finish a first login (found by Codex on #1089).
       *
       * The loop, precisely: guard step 3 sends a user with `needsToAcceptTerms` here; step 4
       * then denied it, because `canAccessRoute` deny-lists every route this role has not opted
       * into; the fallback sent them to DataSharing; step 3 sent them straight back. Infinite.
       *
       * This is the THIRD time this ticket has produced a bootstrap-route blocker — RAP-556 shot
       * `/auth/me` and then `POST /users/me/accept-terms` the same way. The lesson that keeps not
       * sticking: the set to check is every route the app must pass through BEFORE it can render
       * anything, and terms acceptance is in it. A fixture whose user already accepted the terms
       * cannot see any of them, which is why all three were found by something other than a test.
       */
      allowGrantAudit: true,
      label: "Terms of Use",
      /**
       * FOURTH time, and the first one caught before shipping. The referring-portal gate
       * (RAP-630) is a second deny-by-default rule over the same matched chain, so it re-creates
       * the identical loop for a different principal: step 3 sends a portal user with unaccepted
       * terms here, the portal rule denies every route without `referringPortal`, the fallback
       * sends them to the portal landing page, and step 3 sends them straight back.
       *
       * Two flags on one route is the honest cost of two deny rules. If a third arrives, the
       * answer is a single `bootstrap: true` that every deny rule admits — not a third flag.
       */
      referringPortal: true,
    },
    name: "AcceptTerms",
    path: "/terms",
  },
];

/**
 * Public routes — accessible without authentication.
 * Each route MUST explicitly set `meta.public: true`.
 */
const PUBLIC_ROUTES: RouteRecordRaw[] = [
  {
    component: () => import("@/views/auth/SignIn.vue"),
    meta: { label: "Sign In", public: true },
    name: "SignIn",
    path: "/auth/login",
  },
  {
    component: () => import("@/views/auth/TwoStepVerification.vue"),
    meta: { label: "Verify Code", public: true },
    name: "TwoStepVerification",
    path: "/auth/verify", // Simplified path without params
    props: (route) => ({ email: route.query.email }), // Pass query as props
  },
  {
    component: () => import("@/views/auth/MagicLinkRedeem.vue"),
    meta: { label: "Magic Link", public: true },
    name: "MagicLinkRedeem",
    path: "/auth/magic/:token",
  },
  {
    component: () => import("@/views/auth/Unsubscribe.vue"),
    meta: { label: "Unsubscribe", public: true },
    name: "Unsubscribe",
    path: "/unsubscribe",
  },
  {
    component: () => import("@/views/errors/FourZeroFour.vue"),
    meta: { label: "404 Error", public: true },
    name: "404 Error",
    path: "/error-404",
  },
  {
    component: () => import("@/views/errors/FiveZeroThree.vue"),
    meta: { label: "Service Unavailable", public: true },
    name: "503 Error",
    path: "/error-503",
  },
  {
    component: () => import("@/views/errors/FiveZeroZero.vue"),
    meta: { label: "500 Error", public: true },
    name: "500 Error",
    path: "/error-500",
  },
  {
    component: () => import("@/views/errors/FourZeroFour.vue"),
    meta: { label: "404 Error", public: true },
    name: "NotFound",
    path: "/:catchAll(.*)",
  },
];

/**
 * Deep Insights access. Mirrors the backend `/deep-insights` preHandler
 * (backend/src/routes/deepInsights/index.ts): CORE_ACCESS plus the org-level aiEnabled +
 * dataSyncEnabled kill switches. Externals fail this naturally — both flags read
 * `user.organization?.…` which is null for them.
 *
 * ADMINISTER_AI added 2026-08-23 (RAP-555): the run-scoped endpoints these pages call —
 * `/deep-insights/runs/:runId/{status,results}` — require it server-side, because the results
 * carry client names and AI output for every case in a run with no per-case view filtering, and
 * the feature is ruled deprecated so it will never get that filtering. The per-case insights on
 * the case-detail page are unaffected — that endpoint is not gated on ADMINISTER_AI.
 *
 * Shared by the Deep Insights route and the /ai-assistance section gate.
 */
function canAccessDeepInsights(): boolean {
  const a = useAuthStore();
  return (
    a.hasPermission(PERMISSIONS.CORE_ACCESS) &&
    a.hasPermission(PERMISSIONS.ADMINISTER_AI) &&
    a.isDataSyncEnabled &&
    a.isAiEnabled
  );
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...AUTH_ROUTES, ...PUBLIC_ROUTES],
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { left: 0, top: 0 };
  },
});

router.beforeEach(navigationGuard);

// Analytics: Track page views
import { trackPage, getPageName } from "@/services/analytics";
import { refreshFeatureFlagsIfStale } from "@/services/analytics/featureFlags";

router.afterEach((to, from, failure) => {
  const meta = to.meta;
  const routeName = typeof to.name === "string" ? to.name : undefined;
  const titleText = meta.pageTitle ?? meta.label ?? routeName ?? "Untitled";
  document.title = `${titleText} | Handled RAP`;
  // Keeps kill switches (ai_chat_disabled) live in long-lived tabs; throttled to FLAG_STALE_MS.
  refreshFeatureFlagsIfStale();

  if (!isPageView(to, from, failure)) return;
  trackPage(getPageName(routeName ?? ""), {
    campaignId: to.params.campaignId || to.params.id,
    caseId: to.params.caseId,
    path: to.path,
    referrer: from.fullPath,
    routeName: routeName ?? "",
  });
});

export default router;
